import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import Typography from '@mui/material/Typography'
import { UseQueryResult } from '@tanstack/react-query'
import React from 'react'
import { useTranslation } from 'react-i18next'

import QuickSearchEmptyState from './QuickSearchEmptyState'
import QuickSearchFooter from './QuickSearchFooter'
import { SubAccountKind, SubAccountSearchQuery } from '@/generated/graphql'
import { Noop } from '@/utils'

interface Props {
  query: string
  selectedIndex: number
  subAccountQuery: string
  subAccountSearchQuery: UseQueryResult<SubAccountSearchQuery, unknown>
  onSelect: (item: NonNullable<SubAccountSearchQuery['subAccountSearch']['result']>) => void
}

export default function QuickSearchSubAccountContent(props: Props): JSX.Element {
  const { selectedIndex, subAccountQuery, subAccountSearchQuery, onSelect } = props
  const { t } = useTranslation()

  // Loading state
  if (subAccountSearchQuery.isFetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={13}>
        <CircularProgress />
      </Box>
    )
  }

  // Error state
  if (subAccountSearchQuery.isError) {
    const refetchButton = (
      <Button onClick={subAccountSearchQuery.refetch as Noop}>
        {t('retry')}
      </Button>
    )

    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={13}>
        <Alert severity="error" action={refetchButton}>
          {(subAccountSearchQuery.error as Error)?.message ?? t('oopsSomethingWentWrong')}
        </Alert>
      </Box>
    )
  }

  // User hasn't searched yet
  if (!subAccountQuery.trim()) {
    return (
      <QuickSearchEmptyState
        title={t('quickSearchEmptySubAccountTitle')}
        subtitle={t('quickSearchEmptySubAccountSubtitle')}
      />
    )
  }

  // No results found
  const subAccountSearch = subAccountSearchQuery.data?.subAccountSearch
  if (!subAccountSearch?.result || subAccountSearch.totalCount === 0) {
    return (
      <QuickSearchEmptyState
        title={t('quickSearchNoResultsFoundForQuery', { query: subAccountQuery })}
        subtitle={t('quickSearchNoResultsSubAccountSubtitle')}
      />
    )
  }

  // Destructure result data
  const {
    kind,
    accountNumber,
    businessName,
    leaseMasterAccountNumber,
    telecomAccountNumber,
    taxArea,
  } = subAccountSearch.result

  const masterAccountNumber = (
    telecomAccountNumber || leaseMasterAccountNumber
  )

  const accountType = ({
    [SubAccountKind.LeaseMaster]: 'Lease Master',
    [SubAccountKind.TelecomMaster]: 'Telecom Master',
    [SubAccountKind.BusinessMaster]: 'Business Master',
  } as Record<SubAccountKind, string>)[kind]

  const resultCount = subAccountSearch.totalCount

  const handleListItemClick = (): void => {
    onSelect(subAccountSearch.result as NonNullable<SubAccountSearchQuery['subAccountSearch']['result']>)
  }

  // Results view
  return (
    <>
      <List sx={{ maxHeight: 500, overflow: 'auto', py: 1 }}>
        <ListItem
          sx={{
            flexDirection: 'column',
            alignItems: 'flex-start',
            px: 2,
            py: 1.5,
            cursor: 'pointer',
            backgroundColor: selectedIndex === 0 ? 'action.hover' : 'transparent',
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
          onClick={handleListItemClick}
          tabIndex={0}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <WorkOutlineIcon sx={{ color: 'text.secondary', fontSize: 20 }} />

            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {accountNumber} - {businessName}
            </Typography>
            {taxArea && <Chip label={`${t('taxArea')}: ${taxArea}`} />}
          </Box>

          {masterAccountNumber && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                pl: 3.5,
              }}
            >
              {t('quickSearchSubAccountBelongsToAccount', {
                accountType,
                accountNumber: masterAccountNumber,
              })}
            </Typography>
          )}
        </ListItem>
      </List>

      <Divider />

      <QuickSearchFooter
        resultCount={resultCount}
        keyboardActions={[
          {
            hints: [{ icon: '↑' }, { icon: '↓' }],
            description: 'navigate',
          },
          {
            hints: [{ icon: '↵' }],
            description: 'select',
          },
        ]}
      />
    </>
  )
}

